import { AuctionListPdfFilterParams, AuctionListQueryParams } from './apiRoutes/auctionListQueryParams';
import { AuctionLotsQueryParams } from './apiRoutes/auctionLotsQueryParams';
import { createApiRoute } from './apiRoutes/createApiRoute';
import { MarketReportQueryParams } from './apiRoutes/marketReportQueryParams';
import { encodeArrayForQueryString } from '@cksoftware/react-base';

const apiBuilder = {
  util: {
    selectOption: {
      enumType: (type: string) => `/selectOption/enum/${type}`
    }
  }
};

export const apiRoutes = {
  util: {
    selectOption: {
      liveStreams: '/selectOption/livestreams',
      audioStreams: '/selectOption/audiostreams',
      listingMarketLiveStreams: (listingMarketId: number) =>
        `/selectOption/listingmarket/${listingMarketId}/livestreams`
    }
  },
  auctioneer: {
    list: '/auctioneer/list',
    start: (auctionId: number) => `/auctioneer/${auctionId}/start`
  },
  liveAuction: {
    hub: '/auction/live/hub',
    get: (auctionId: number, isAuctioneer: boolean) => `auction/live/${auctionId}/${isAuctioneer}`
  },
  listingMarketContact: {
    base: 'listingMarketContacts'
  },
  auctionReports: {
    get: '/auctionReports',
    getPdf: (id: number, includeDetail: boolean) =>
      `/AuctionReports/${id}/Pdf?IncludeDetails=${includeDetail ? 'true' : 'false'}`
  },
  auctionLotReport: {
    get: `/auctionLotReports`,
    getPdf: (id: number) => `/AuctionLotReports/${id}/Pdf`
  },
  auctions: {
    list: createApiRoute<AuctionListQueryParams>('auctions'),
    getLots: (id: number) => createApiRoute<AuctionLotsQueryParams>(`auctions/${id}/lots`),
    getPdf: createApiRoute<AuctionListPdfFilterParams>('auctions/pdf')
  },
  admin: {
    selectList: {
      get: (type: string) => `/admin/selectlist/${type}`,
      post: (type: string) => `/admin/selectlist/${type}`,
      put: (id: number, type: string) => `/admin/selectlist/${type}/${id}`
    }
  },
  streaming: {
    getPublishToken: (streamName: string) => `/streaming/publish/token/${streamName}`,
    getViewerToken: (streamName: string) => `/streaming/subscribe/token/${streamName}`
  },
  buyerApproval: {
    adminAuctions: '/buyerapproval/admin/auctions',
    clientAuctions: '/buyerapproval/client/auctions',
    approvalsForAuction: (auctionId: number) => `/buyerapproval/admin/auction/${auctionId}`,
    approveBuyer: (auctionId: number, approvalId: number) =>
      `/buyerapproval/auction/${auctionId}/approve/${approvalId}`,
    denyBuyer: (auctionId: number, approvalId: number) => `/buyerapproval/auction/${auctionId}/deny/${approvalId}`,
    createBuyers: (auctionId: number, profileIds: number[]) =>
      `/buyerapproval/auction/${auctionId}/addbuyers?${encodeArrayForQueryString('profileIds', profileIds)}`,
    requestApproval: (auctionId: number) => `/buyerapproval/auction/${auctionId}/request`
  },
  userAdmin: {
    users: {
      get: (userId: string) => `/admin/user/${userId}`,
      profiles: {
        post: (userId: string) => `/admin/user/${userId}/profile`,
        put: (userId: string, profileId: number) => `/admin/user/${userId}/profile/${profileId}`
      },
      createProfileUser: '/admin/user/CreateProfileUser',
      selectRoles: '/admin/user/RoleSelectOptions',
      getFiltered: '/admin/user/filtered',
      getFilteredProfiles: '/admin/user/filtered/profiles'
    }
  },
  auth: {
    register: '/auth/user/CreateProfileUser',
    verifyEmail: '/auth/user/VerifyEmail',
    myAccount: '/myAccount',
    profiles: {
      setActive: (profileId: number) => `/auth/user/profiles/setactive/${profileId}`,
      put: (profileId: number) => `/auth/user/profiles/${profileId}`
    }
  },
  consigner: {
    auctions: {
      base: '/consignerauctions',
      getLots: `/consignerlots`
    }
  },
  crm: {
    advertisements: {
      base: 'advertisements'
    },
    advertisementsAdmin: {
      base: 'admin/advertisements'
    },
    banners: {
      base: 'banners'
    },
    bannersAdmin: {
      base: 'admin/banners'
    },
    contactPageEntries: {
      base: 'contact-page-entries'
    },
    contactPageEntriesAdmin: {
      base: 'contact-page-entries/admin'
    },
    auctions: {
      base: 'admin/auctions',
      getLots: (id: number) => `admin/auctions/${id}/lots`
    },
    auctionDefaults: {
      pureBred: 'admin/auctionDefaults/PureBred',
      presort: 'admin/auctionDefaults/Presort',
      direct: 'admin/auctionDefaults/Direct',
      event: 'admin/auctionDefaults/Event',
      timed: 'admin/auctionDefaults/Timed'
    },
    lots: {
      base: 'admin/lots',
      put: (auctionId: number) => `admin/lots/${auctionId}`
    },
    listingMarkets: {
      base: 'listingMarkets',
      select: 'listingMarkets/select'
    },
    liveSales: {
      base: 'admin/liveSales',
      getLots: (id: number) => `admin/liveSales/${id}/lots`,
      getAttendees: (id: number) => `admin/liveSales/${id}/attendees`
    },
    liveSaleLots: {
      get: `admin/liveSaleLots`,
      put: (liveSaleId: number) => `admin/liveSaleLots/${liveSaleId}`
    },
    saleFunctionalType: {
      select: apiBuilder.util.selectOption.enumType('AuctionType')
    },
    saleStatus: {
      select: apiBuilder.util.selectOption.enumType('SaleStatus')
    },
    advertisementPosition: {
      select: apiBuilder.util.selectOption.enumType('AdvertisementPosition')
    },
    priceType: {
      select: apiBuilder.util.selectOption.enumType('PriceType')
    },
    buyerFeeType: {
      select: apiBuilder.util.selectOption.enumType('BuyerFeeType')
    },
    priceSlideType: {
      select: apiBuilder.util.selectOption.enumType('PriceSlideType')
    },
    months: {
      select: apiBuilder.util.selectOption.enumType('Month')
    },
    buyers: {
      select: 'admin/buyers/select'
    }
  },
  attendeeLog: {
    get: (auctionId: number) => `/attendee-log/${auctionId}`
  },
  marketReports: {
    list: createApiRoute<MarketReportQueryParams>('marketReports'),
    detail: (id: string | undefined) => `/marketReportDetail/${id}`,
    getPdf: (id: string | undefined, isLandscape: boolean) => `/marketReportDetail/${id}/${isLandscape}/Pdf`
  },
  uploadLots: {
    post: (auctionId: string) => `/auction/lotsupload/${auctionId}`
  },
  reports: {
    chatLog: (auctionId: number) => `/Admin/Auctions/${auctionId}/chat-log`
  }
};
